import React, { useEffect, useState } from 'react';
import { Card, PageHeader, Button } from '../../components/shared/ui';
import { adminAPI, extendedAdminAPI } from '../../utils/api';
import api from '../../utils/api';
import useIsMobile from '../../utils/useIsMobile';

const SCORE_COLOR = (s) => s >= 70 ? '#059669' : s >= 45 ? '#d97706' : '#6b7280';
const LEVEL_COLOR = { junior: '#6b7280', mid: '#2563eb', senior: '#7c3aed' };
const LEVEL_LABEL = { junior: 'Junior', mid: 'Mid', senior: 'Senior' };

// Atanan öğrenci listesi: durum etiketi/rengi
const ASG_STATUS_LABEL = { shortlisted: 'Kısa Liste', invited: 'Davet Edildi', accepted: 'Kabul Edildi' };
const ASG_STATUS_STYLE = {
  shortlisted: { background: '#fef9c3', color: '#92400e' },
  invited: { background: '#eff6ff', color: '#2563eb' },
  accepted: { background: '#f0fdf4', color: '#16a34a' },
};

const ScoreBadge = ({ value, label, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ width: 40, height: 40, borderRadius: '50%', border: `2.5px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color, margin: '0 auto 2px' }}>{value}</div>
    <div style={{ fontSize: 10, color: '#9ca3af' }}>{label}</div>
  </div>
);

const AssignmentPanel = () => {
  const isMobile = useIsMobile();
  const [positions, setPositions] = useState([]);
  const [selectedPos, setSelectedPos] = useState(null);
  const [students, setStudents] = useState([]);
  const [positionDetail, setPositionDetail] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState('');

  // "Tüm Atananlar" liste modalı
  const [showAll, setShowAll] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [allSearch, setAllSearch] = useState('');
  const [allStatus, setAllStatus] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/admin/assignment/positions').then(r => setPositions(r.data)).catch(() => {});
  }, []);

  const selectPosition = async (pos) => {
    setSelectedPos(pos);
    setStudents([]);
    setLoadingStudents(true);
    try {
      const r = await api.get(`/admin/assignment/positions/${pos.id}/students`);
      setStudents(r.data.students || []);
      setPositionDetail(r.data.position);
    } catch { }
    setLoadingStudents(false);
  };

  const handleInvite = async (student) => {
    if (!selectedPos) return;
    setInviting(student.id);
    try {
      await extendedAdminAPI.assignStudent({ student_id: student.id, position_id: selectedPos.id, message });
      setToast(`${student.first_name} ${student.last_name} davet edildi`);
      setStudents(prev => prev.map(s => s.id === student.id ? {
        ...s,
        already_invited: true,
        assignments: [
          ...(s.assignments || []),
          { status: 'invited', position_id: selectedPos.id, position_title: selectedPos.title, company_name: selectedPos.company_name },
        ],
      } : s));
      // Soldaki ilan kartındaki atama sayacını canlı artır
      setPositions(prev => prev.map(p => p.id === selectedPos.id ? { ...p, assigned_count: (p.assigned_count || 0) + 1 } : p));
    } catch (e) {
      setToast('Hata: ' + (e.response?.data?.error || 'Davet gönderilemedi'));
    }
    setTimeout(() => setToast(''), 3500);
    setInviting(null);
  };

  const openAllAssigned = async () => {
    setShowAll(true);
    setLoadingAll(true);
    try {
      const r = await extendedAdminAPI.getAssignedStudents();
      setAllRows(r.data || []);
    } catch {
      setToast('Hata: Atanan öğrenciler alınamadı');
      setTimeout(() => setToast(''), 3500);
    }
    setLoadingAll(false);
  };

  const exportAllAssigned = async () => {
    setExporting(true);
    try {
      const r = await extendedAdminAPI.exportAssignedStudents();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'atanan-ogrenciler.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setToast('Hata: Excel indirilemedi');
      setTimeout(() => setToast(''), 3500);
    }
    setExporting(false);
  };

  const renderRow = (st, idx, isAssigned) => {
    const isDone = st.already_invited || (st.assignments && st.assignments.length > 0);
    return (
      <div key={st.id} style={{ padding: '14px 0', borderBottom: '1px solid #f3f4f6', display: 'grid', gridTemplateColumns: isMobile ? '32px 1fr' : '32px 1fr auto', gap: 12, alignItems: 'center', opacity: isAssigned ? 0.85 : 1 }}>
        {/* Sıra */}
        <div style={{ fontSize: 13, fontWeight: 700, color: isAssigned ? '#9ca3af' : (idx < 3 ? '#2563eb' : '#9ca3af'), textAlign: 'center' }}>{isAssigned ? '•' : `#${idx + 1}`}</div>

        {/* Öğrenci bilgisi */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{st.first_name} {st.last_name}</span>
            {(st.assignments || []).map((as, i) => (
              <span key={i} style={{ fontSize: 10, padding: '1px 8px', borderRadius: 20, fontWeight: 600,
                background: as.status === 'accepted' ? '#f0fdf4' : '#eff6ff', color: as.status === 'accepted' ? '#16a34a' : '#2563eb' }}>
                → {as.company_name}{as.status === 'accepted' ? ' (Kabul)' : ' (Davet)'}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{st.university} {st.department ? `· ${st.department}` : ''} {st.city ? `· ${st.city}` : ''}</div>
          {st.grade && <div style={{ fontSize: 12, color: '#9ca3af' }}>{st.grade}. Sınıf{st.gpa ? ` · GPA: ${st.gpa}` : ''}</div>}

          {/* Eşleşen beceriler */}
          {st.matched_skills?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
              {st.matched_skills.map(s => (
                <span key={s} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: '#f0fdf4', color: '#16a34a', fontWeight: 600 }}>✓ {s}</span>
              ))}
            </div>
          )}

          {/* AI Özeti */}
          {st.llm_summary && (
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: '#f5f3ff', border: '1px solid #e9d5ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed' }}>✦ AI Analizi</span>
                {st.llm_level && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#ede9fe', color: LEVEL_COLOR[st.llm_level] || '#6b7280', fontWeight: 600 }}>
                    {LEVEL_LABEL[st.llm_level] || st.llm_level}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#4c1d95', lineHeight: 1.5 }}>{st.llm_summary}</div>
              {st.llm_strengths?.length > 0 && (
                <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {st.llm_strengths.map((s, i) => (
                    <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#d1fae5', color: '#065f46' }}>+ {s}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Skorlar + Davet */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
          <ScoreBadge value={st.position_match} label="Beceri" color={SCORE_COLOR(st.position_match)} />
          <ScoreBadge value={st.profile_score} label="Profil" color={SCORE_COLOR(st.profile_score)} />
          <ScoreBadge value={st.cv_keyword_score ?? '—'} label="CV" color="#7c3aed" />
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: SCORE_COLOR(st.combined_score), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', margin: '0 auto 2px' }}>{st.combined_score}</div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>Kombine</div>
          </div>
          <button
            disabled={isDone || inviting === st.id}
            onClick={() => handleInvite(st)}
            style={{ padding: '8px 16px', background: isDone ? '#f3f4f6' : inviting === st.id ? '#93c5fd' : '#2563eb', color: isDone ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: isDone ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            {st.already_invited ? '✓ Davet Edildi' : isDone ? '✓ Atanmış' : inviting === st.id ? '...' : 'Davet Et'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <PageHeader title="Atama Paneli" description="İlana göre öğrenci sırala ve davet et" />
        <button onClick={openAllAssigned}
          style={{ padding: '10px 18px', background: '#111827', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          📋 Tüm Atananlar
        </button>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', background: toast.startsWith('Hata') ? '#fef2f2' : '#f0fdf4', color: toast.startsWith('Hata') ? '#dc2626' : '#16a34a' }}>
          {toast}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Sol: İlanlar */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Açık İlanlar ({positions.length})</div>
          {positions.length === 0 && (
            <Card><p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 16 }}>Aktif ilan yok.</p></Card>
          )}
          {positions.map(pos => (
            <div key={pos.id} onClick={() => selectPosition(pos)}
              style={{ padding: '14px 16px', borderRadius: 10, border: `2px solid ${selectedPos?.id === pos.id ? '#2563eb' : '#e5e7eb'}`, background: selectedPos?.id === pos.id ? '#eff6ff' : '#fff', cursor: 'pointer', marginBottom: 8, transition: 'all 0.15s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{pos.title}</div>
                {(() => {
                  const a = pos.assigned_count || 0, q = pos.quota || 0;
                  const full = q > 0 && a >= q;
                  return (
                    <span title={`${a} atanmış / ${q} kontenjan`} style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap',
                      background: full ? '#dcfce7' : a > 0 ? '#eff6ff' : '#f3f4f6',
                      color: full ? '#15803d' : a > 0 ? '#2563eb' : '#9ca3af' }}>
                      👥 {a}/{q}{full ? ' ·Doldu' : ''}
                    </span>
                  );
                })()}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{pos.company_name}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {pos.sector && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280' }}>{pos.sector}</span>}
                {pos.internship_type && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: '#eff6ff', color: '#2563eb' }}>{pos.internship_type}</span>}
                {pos.is_paid && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: '#f0fdf4', color: '#16a34a' }}>Ücretli</span>}
              </div>
              {(pos.preferred_skills || []).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {pos.preferred_skills.slice(0, 3).map(s => (
                    <span key={s} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#fef9c3', color: '#92400e' }}>{s}</span>
                  ))}
                  {pos.preferred_skills.length > 3 && <span style={{ fontSize: 10, color: '#9ca3af' }}>+{pos.preferred_skills.length - 3}</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Sağ: Öğrenciler */}
        <div>
          {!selectedPos ? (
            <Card>
              <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>←</div>
                <p style={{ fontSize: 14 }}>Soldan bir ilan seçin</p>
              </div>
            </Card>
          ) : (
            <>
              {/* İlan özeti */}
              {positionDetail && (
                <Card style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{positionDetail.title}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{positionDetail.company_name} · {positionDetail.duration_weeks ? `${positionDetail.duration_weeks} hafta` : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#374151' }}>
                      {positionDetail.required_min_gpa && <span>Min GPA: <b>{positionDetail.required_min_gpa}</b></span>}
                      {positionDetail.required_min_grade && <span>Min Sınıf: <b>{positionDetail.required_min_grade}</b></span>}
                      {positionDetail.required_city && <span>Şehir: <b>{positionDetail.required_city}</b></span>}
                      <span>Kontenjan: <b>{positionDetail.quota}</b></span>
                    </div>
                  </div>
                  {(positionDetail.preferred_skills || []).length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      <span style={{ fontSize: 12, color: '#6b7280', marginRight: 4 }}>İstenen beceriler:</span>
                      {positionDetail.preferred_skills.map(s => (
                        <span key={s} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef9c3', color: '#92400e', fontWeight: 500 }}>{s}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Davet mesajı (opsiyonel)</label>
                    <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Öğrenciye iletilecek not..."
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </Card>
              )}

              {/* Öğrenci listesi */}
              <Card>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                  {loadingStudents ? 'Yükleniyor...' : `${students.length} öğrenci sıralandı`}
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>Kombine Skor = %45 Beceri Eşleşmesi + %35 Profil + %20 CV</span>
                </div>

                {loadingStudents && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Analiz ediliyor...</div>}

                {!loadingStudents && (() => {
                  const available = students.filter(s => !(s.assignments && s.assignments.length > 0));
                  const assigned = students.filter(s => s.assignments && s.assignments.length > 0);
                  const groupHeader = (text) => (
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, margin: '6px 0 4px', paddingTop: 6 }}>{text}</div>
                  );
                  return (
                    <>
                      {available.length === 0 && assigned.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>Uygun öğrenci bulunamadı.</div>
                      )}
                      {available.length > 0 && groupHeader(`Boştaki Adaylar (${available.length})`)}
                      {available.map((st, idx) => renderRow(st, idx, false))}
                      {assigned.length > 0 && groupHeader(`✓ Atananlar (${assigned.length})`)}
                      {assigned.map((st, idx) => renderRow(st, idx, true))}
                    </>
                  );
                })()}
              </Card>
            </>
          )}
        </div>
      </div>
      {/* === Tüm Atananlar liste modalı === */}
      {showAll && (() => {
        const filtered = allRows.filter(r => {
          if (allStatus !== 'all' && r.status !== allStatus) return false;
          if (allSearch.trim()) {
            const q = allSearch.toLowerCase();
            const hay = `${r.first_name || ''} ${r.last_name || ''} ${r.email || ''} ${r.company_name || ''} ${r.position_title || ''} ${r.university || ''}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        });
        const groups = [];
        const gidx = {};
        filtered.forEach(r => {
          const key = r.company_name || '—';
          if (gidx[key] === undefined) { gidx[key] = groups.length; groups.push({ company: key, rows: [] }); }
          groups[gidx[key]].rows.push(r);
        });
        const counts = { all: allRows.length, shortlisted: 0, invited: 0, accepted: 0 };
        allRows.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
        return (
          <div onClick={() => setShowAll(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: isMobile ? 0 : '40px 20px', overflowY: 'auto' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: isMobile ? 0 : 14, width: '100%', maxWidth: 880, minHeight: isMobile ? '100vh' : 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, position: 'sticky', top: 0, background: '#fff', borderRadius: isMobile ? 0 : '14px 14px 0 0', zIndex: 2 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Atanan Öğrenciler</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{loadingAll ? 'Yükleniyor...' : `${filtered.length} / ${allRows.length} kayıt · ${groups.length} şirket`}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={exportAllAssigned} disabled={exporting || !allRows.length}
                    style={{ padding: '8px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: allRows.length ? 'pointer' : 'not-allowed', opacity: allRows.length ? 1 : 0.5, whiteSpace: 'nowrap' }}>
                    {exporting ? '...' : '⬇ Excel'}
                  </button>
                  <button onClick={() => setShowAll(false)}
                    style={{ padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Kapat</button>
                </div>
              </div>
              <div style={{ padding: '14px 22px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f9fafb' }}>
                <input value={allSearch} onChange={e => setAllSearch(e.target.value)} placeholder="İsim, şirket, pozisyon ara..."
                  style={{ flex: 1, minWidth: 180, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[['all', `Tümü (${counts.all})`], ['accepted', `Kabul (${counts.accepted})`], ['invited', `Davet (${counts.invited})`], ['shortlisted', `Kısa Liste (${counts.shortlisted})`]].map(([val, lbl]) => (
                    <button key={val} onClick={() => setAllStatus(val)}
                      style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${allStatus === val ? '#2563eb' : '#e5e7eb'}`, background: allStatus === val ? '#eff6ff' : '#fff', color: allStatus === val ? '#2563eb' : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div style={{ padding: '8px 22px 22px' }}>
                {loadingAll && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Yükleniyor...</div>}
                {!loadingAll && filtered.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>Kayıt bulunamadı.</div>}
                {!loadingAll && groups.map(g => (
                  <div key={g.company} style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', padding: '6px 0', borderBottom: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}>
                      <span>🏢 {g.company}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{g.rows.length} öğrenci</span>
                    </div>
                    {g.rows.map(r => {
                      const stl = ASG_STATUS_STYLE[r.status] || { background: '#f3f4f6', color: '#6b7280' };
                      return (
                        <div key={r.application_id} style={{ padding: '10px 0', borderBottom: '1px solid #f9fafb', display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '1fr 1fr auto', gap: 10, alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{r.first_name} {r.last_name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email}</div>
                          </div>
                          <div style={{ fontSize: 12, color: '#374151' }}>
                            <div style={{ fontWeight: 500 }}>{r.position_title}</div>
                            <div style={{ color: '#9ca3af' }}>{r.university || ''}{r.department ? ` · ${r.department}` : ''}{r.grade ? ` · ${r.grade}.sınıf` : ''}</div>
                          </div>
                          <span style={{ ...stl, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap', justifySelf: 'end' }}>
                            {ASG_STATUS_LABEL[r.status] || r.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default AssignmentPanel;
